import create from 'zustand';
import type { Subscription } from '@rails/actioncable';
import consumer from '@/channels/consumer';
import { logLiveSessionEvent } from '@/modules/liveSessionEvents';
import type { LiveSessionMessage } from './messages';

// Module-scoped store: one LiveSessionChannel subscription per session_id, refcounted
// across React trees. Switching session_id tears down the old subscription and creates
// a new one.

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'rejected';

interface StudentCounts {
  connectedCount: number;
  connectedIds: number[];
  invitedIds: number[];
}

interface CurrentSlide {
  slideId: number;
  slideType: string;
}

interface TeacherConnectionEvent {
  // Carries the persisted slide payload on teacher_reconnected so students can snap back.
  // null for teacher_disconnected.
  slideId: number | null;
  slideType: string | null;
  // Monotonically increasing so consumers can react to repeated events even when the slide
  // payload is identical (e.g. teacher reconnects without having navigated).
  occurredAt: number;
}

interface LiveSessionState {
  sessionId: string | null;
  connectionState: ConnectionState | null;
  currentSlide: CurrentSlide | null;
  studentCounts: StudentCounts;
  teacherDisconnectedAt: TeacherConnectionEvent | null;
  teacherReconnectedAt: TeacherConnectionEvent | null;
  sessionEndedReason: string | null;
}

interface LiveSessionActions {
  attach: (sessionId: string) => () => void;
  clearSessionEnded: () => void;
  performNavigate: (slideId: number, slideType: string, position?: number | null) => void;
  markPresent: () => void;
  markAbsent: () => void;
}

interface InternalState extends LiveSessionState, LiveSessionActions {
  refCount: number;
  subscription: Subscription | null;
}

const initialCounts: StudentCounts = {
  connectedCount: 0,
  connectedIds: [],
  invitedIds: [],
};

const cleanState = (): Partial<LiveSessionState> => ({
  connectionState: null,
  currentSlide: null,
  sessionEndedReason: null,
  sessionId: null,
  studentCounts: initialCounts,
  teacherDisconnectedAt: null,
  teacherReconnectedAt: null,
});

const useLiveSessionStore = create<InternalState>((set, get) => {
  // PLAT-17411 — Action Cable's stale detection takes 20-40s to notice a silently dead network
  // (wifi off), so while subscribed the browser's offline/online events flip the state and force
  // the reconnect directly. The channel callbacks below stay as the fallback for drops
  // navigator.onLine can't see (e.g. an upstream router dying).
  const handleOffline = () => {
    const { connectionState, sessionId } = get();
    if (connectionState !== 'connected' && connectionState !== 'connecting') return;
    set({ connectionState: 'disconnected' });
    logLiveSessionEvent(sessionId, 'ws_disconnected', { offline_event: true, online: false });
  };

  // No state change here — reopen() resubscribes and the connected() callback confirms recovery.
  // Guarded so a spurious online event on a healthy socket doesn't churn the shared consumer.
  const handleOnline = () => {
    if (get().connectionState !== 'disconnected') return;
    consumer.connection.reopen();
  };

  // Intentionally idempotent (stable refs — re-adding is a no-op), so attach can call it
  // unconditionally and self-heal rather than gating on refCount bookkeeping.
  const addNetworkListeners = () => {
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
  };

  const removeNetworkListeners = () => {
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
  };

  return {
    attach: (sessionId) => {
      const state = get();

      if (state.sessionId === sessionId && state.subscription) {
        set({ refCount: state.refCount + 1 });
      } else {
        // Tear down a stale subscription explicitly so the server doesn't have to dedupe two
        // unsubscribe/subscribe pairs from the same user when session_id changes mid-mount.
        state.subscription?.unsubscribe();

        const subscription = consumer.subscriptions.create(
          { channel: 'LiveSessionChannel', session_id: sessionId },
          {
            connected() {
              set({ connectionState: 'connected' });
              logLiveSessionEvent(sessionId, 'ws_connected');
            },
            // The offline listener above usually gets here first; log the drop once.
            disconnected() {
              if (get().connectionState === 'disconnected') return;
              set({ connectionState: 'disconnected' });
              logLiveSessionEvent(sessionId, 'ws_disconnected', {
                offline_event: false,
                online: navigator.onLine,
              });
            },
            received(data: LiveSessionMessage) {
              if (data?.type === 'navigate_to_slide') {
                set({
                  currentSlide: { slideId: data.slide_id, slideType: data.slide_type },
                });
              } else if (data?.type === 'student_count_changed') {
                set(prev => ({
                  studentCounts: {
                    connectedCount: data.connected_count ?? 0,
                    connectedIds: data.connected_ids ?? prev.studentCounts.connectedIds,
                    invitedIds: data.invited_ids ?? prev.studentCounts.invitedIds,
                  },
                }));
              } else if (data?.type === 'teacher_disconnected') {
                set({
                  teacherDisconnectedAt: {
                    occurredAt: Date.now(),
                    slideId: null,
                    slideType: null,
                  },
                });
              } else if (data?.type === 'teacher_reconnected') {
                set({
                  teacherReconnectedAt: {
                    occurredAt: Date.now(),
                    slideId: data.slide_id ?? null,
                    slideType: data.slide_type ?? null,
                  },
                });
              } else if (data?.type === 'session_ended') {
                set({ sessionEndedReason: data.end_reason ?? 'session_ended' });
                logLiveSessionEvent(sessionId, 'session_ended_received', { end_reason: data.end_reason ?? null });
              }
            },
            rejected() {
              set({ connectionState: 'rejected' });
              logLiveSessionEvent(sessionId, 'ws_rejected');
            },
          },
        );

        addNetworkListeners();

        set({
          ...cleanState(),
          connectionState: 'connecting',
          refCount: 1,
          sessionId,
          subscription,
        });
      }

      return () => {
        const current = get();

        // This detach belongs to the captured sessionId. If the store has since switched
        // sessions, the old subscription was already torn down — no-op for the new session.
        if (current.sessionId !== sessionId) return;

        const remaining = current.refCount - 1;
        if (remaining > 0) {
          set({ refCount: remaining });
          return;
        }

        current.subscription?.unsubscribe();
        removeNetworkListeners();
        set({
          ...cleanState(),
          refCount: 0,
          // PLAT-17536 — preserve past teardown: useStudentSlideSync detaches the instant a session
          // ends, and clearing here would round-trip null→ended→null before the separate-root
          // StudentSessionListener's effect runs, coalescing the popup away. A fresh attach resets it.
          sessionEndedReason: current.sessionEndedReason,
          subscription: null,
        });
      };
    },

    // PLAT-17536 — StudentSessionListener clears the preserved reason when its popup is dismissed.
    clearSessionEnded: () => set({ sessionEndedReason: null }),

    connectionState: null,
    currentSlide: null,

    markAbsent: () => {
      get().subscription?.perform('mark_absent');
    },
    // PLAT-17441 — presence is an explicit action, fired by the slideshow view only (not the
    // layout listener). Re-fired on reconnect; the server's broadcast-if-added gate keeps a
    // redundant mark from a still-present student from spamming the count.
    markPresent: () => {
      get().subscription?.perform('mark_present');
    },

    performNavigate: (slideId, slideType, position = null) => {
      get().subscription?.perform('navigate_to_slide', {
        slide_id: slideId,
        slide_type: slideType,
        ...(Number.isFinite(position) ? { position } : {}),
      });
    },

    refCount: 0,
    sessionEndedReason: null,
    sessionId: null,
    studentCounts: initialCounts,
    subscription: null,
    teacherDisconnectedAt: null,
    teacherReconnectedAt: null,
  };
});

export default useLiveSessionStore;
